import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Anchor, ArrowRight, Mail, Lock, Loader2, UserPlus, LogIn, AtSign } from "lucide-react";
import { signupEmail, loginEmail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

/**
 * Unified email+password auth page. The `mode` prop decides whether we
 * render the signup or signin shape — both go through this component so
 * shared chrome (header, Google alternate, footer) stays consistent.
 */
function EmailAuthForm({ mode }) {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const isSignup = mode === "signup";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const fn = isSignup ? signupEmail : loginEmail;
      const payload = isSignup
        ? { email: email.trim(), password, name: name.trim() || null }
        : { email: email.trim(), password };
      const res = await fn(payload);
      setUser?.(res.user);
      toast.success(isSignup ? "Welcome to the Sentinel." : "Welcome back.");
      // New signups → onboarding to claim a @handle. Existing users
      // who already have one go to /welcome — that page is tier-aware
      // and routes architects to /console, free operators to /truths.
      const dest = res.user?.username_set ? "/welcome" : "/onboarding";
      navigate(dest, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#09090b] text-zinc-100 relative flex flex-col">
      <div className="absolute inset-0 scanline pointer-events-none" />

      <header className="relative z-10 flex items-center justify-between px-8 py-6">
        <Link to="/" className="flex items-center gap-3" data-testid="auth-home-link">
          <div className="h-9 w-9 border border-emerald-500/50 flex items-center justify-center">
            <Anchor className="h-4 w-4 text-emerald-400" />
          </div>
          <div className="leading-tight">
            <div className="font-heading text-sm tracking-[0.3em]">PROJECTIVY</div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Sentinel · v18.48</div>
          </div>
        </Link>
      </header>

      <div className="relative z-10 flex-1 flex items-center justify-center px-6 py-10">
        <div className="max-w-md w-full border border-zinc-800 bg-[#0b0b0d] p-8 sm:p-10">
          <div className="text-[10px] uppercase tracking-[0.35em] text-emerald-400 mb-3 flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-sentinel-pulse" />
            {isSignup ? "Initiate Docking" : "Welcome back"}
          </div>
          <h1 className="font-heading text-3xl lg:text-4xl leading-tight">
            {isSignup ? (
              <>
                New here?<br /><span className="text-zinc-500">Sign up first.</span>
              </>
            ) : (
              <>
                Sign in<br /><span className="text-zinc-500">to the Sentinel.</span>
              </>
            )}
          </h1>
          <p className="text-xs text-zinc-400 mt-5 leading-relaxed">
            {isSignup ? (
              <>
                Use any email and a password of your choice. You will pick a public{" "}
                <AtSign className="inline h-3 w-3 -mt-0.5 text-amber-400" />
                <span className="font-mono text-amber-300">handle</span> right after, so others can send AEGIS to your wallet.
              </>
            ) : (
              "Enter the email and password you signed up with. Google sign-in works too if that is what you used originally."
            )}
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid={`${mode}-form`}>
            {isSignup && (
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1.5">Display name <span className="text-zinc-700">(optional)</span></div>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="What should we call you?"
                  data-testid={`${mode}-name-input`}
                  className="w-full bg-black border border-zinc-800 focus:border-emerald-500/60 outline-none text-zinc-100 px-3 py-2.5 text-sm font-mono"
                  maxLength={80}
                  autoComplete="name"
                />
              </label>
            )}
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1.5">Email</div>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
                <input
                  required
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid={`${mode}-email-input`}
                  className="w-full bg-black border border-zinc-800 focus:border-emerald-500/60 outline-none text-zinc-100 pl-9 pr-3 py-2.5 text-sm font-mono"
                  autoComplete={isSignup ? "email" : "username"}
                  spellCheck={false}
                />
              </div>
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1.5">
                Password {isSignup && <span className="text-zinc-700">(at least 12 characters)</span>}
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
                <input
                  required
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={isSignup ? "Pick something strong" : "Your password"}
                  data-testid={`${mode}-password-input`}
                  className="w-full bg-black border border-zinc-800 focus:border-emerald-500/60 outline-none text-zinc-100 pl-9 pr-3 py-2.5 text-sm font-mono"
                  autoComplete={isSignup ? "new-password" : "current-password"}
                  minLength={isSignup ? 12 : undefined}
                />
              </div>
            </label>

            {error && (
              <div
                className="border border-rose-500/30 bg-rose-500/5 px-3 py-2 text-[11px] text-rose-300"
                data-testid={`${mode}-error`}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid={`${mode}-submit-btn`}
              className="w-full inline-flex items-center justify-center gap-3 px-6 py-3.5 border border-emerald-500 bg-emerald-500/5 text-emerald-400 hover:bg-emerald-500 hover:text-zinc-950 transition-colors text-xs uppercase tracking-[0.35em] disabled:opacity-50"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : isSignup ? (
                <><UserPlus className="h-4 w-4" /> Create account <ArrowRight className="h-4 w-4" /></>
              ) : (
                <><LogIn className="h-4 w-4" /> Sign in <ArrowRight className="h-4 w-4" /></>
              )}
            </button>
          </form>

          <div className="relative my-6 flex items-center" aria-hidden="true">
            <div className="flex-1 h-px bg-zinc-900" />
            <span className="px-3 text-[9px] uppercase tracking-[0.3em] text-zinc-600">or</span>
            <div className="flex-1 h-px bg-zinc-900" />
          </div>

          <button
            type="button"
            onClick={handleGoogle}
            data-testid={`${mode}-google-btn`}
            className="w-full inline-flex items-center justify-center gap-3 px-6 py-3 border border-zinc-800 text-zinc-300 hover:border-emerald-500/40 hover:text-emerald-300 transition-colors text-[11px] uppercase tracking-[0.3em]"
          >
            Continue with Google
          </button>

          <p className="mt-6 text-center text-[11px] text-zinc-500">
            {isSignup ? (
              <>
                Already have an account?{" "}
                <Link to="/signin" className="text-emerald-400 hover:underline" data-testid="auth-switch-to-signin">Sign in</Link>
              </>
            ) : (
              <>
                New here?{" "}
                <Link to="/signup" className="text-emerald-400 hover:underline" data-testid="auth-switch-to-signup">Sign up</Link>
              </>
            )}
          </p>
        </div>
      </div>

      <footer className="relative z-10 px-8 py-6 text-[10px] uppercase tracking-[0.35em] text-zinc-600 text-center">
        By continuing, you accept the Humanitarian Pulse handshake.
      </footer>
    </div>
  );
}

export function Signup() {
  return <EmailAuthForm mode="signup" />;
}

export function Signin() {
  return <EmailAuthForm mode="signin" />;
}

export default Signup;
